// src/providers/QueryProvider.tsx
// ═══════════════════════════════════════════════════════════════════════════════
// REACT QUERY PROVIDER WITH ERROR HANDLING
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { parseApiError } from "@/core/api/hooks";
import { isAppError, AuthenticationError } from "@/core/errors/classes";
import { globalErrorHandler } from "@/core/errors/handlers";

interface QueryProviderProps {
  children: React.ReactNode;
  onAuthError?: (error: AuthenticationError) => void;
}

/**
 * Create QueryClient with error handling
 */
const createQueryClient = (
  onAuthError?: (error: AuthenticationError) => void,
): QueryClient => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const parsedError = parseApiError(error);

        // Skip cancelled requests
        if (parsedError.isCancelledError) return;

        // Handle auth errors
        if (
          parsedError.isAuthError &&
          isAppError(error) &&
          error instanceof AuthenticationError
        ) {
          onAuthError?.(error);
          return;
        }

        // Log error
        globalErrorHandler.handle(error, {
          showToast: false,
          logError: true,
          reportError: true,
          context: {
            component: "ReactQuery",
            action: `Query: ${query.queryKey.toString()}`,
          },
        });

        // Show toast only for background refetch errors without data
        if (query.state.data === undefined && !parsedError.isCancelledError) {
          // const icon = getErrorIcon(parsedError.category);
          // toast.error(
          //   <div className="flex items-start gap-2">
          //     <span>{icon}</span>
          //     <span>{parsedError.message}</span>
          //   </div>,
          //   { toastId: `query-error-${query.queryKey.toString()}` },
          // );
        }
      },
    }),

    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const parsedError = parseApiError(error);

        // Skip cancelled requests
        if (parsedError.isCancelledError) return;

        // Handle auth errors
        if (
          parsedError.isAuthError &&
          isAppError(error) &&
          error instanceof AuthenticationError
        ) {
          onAuthError?.(error);
          return;
        }

        // Log error
        globalErrorHandler.handle(error, {
          showToast: false,
          logError: true,
          reportError: true,
          context: {
            component: "ReactQuery",
            action: "Mutation",
          },
        });

        // Show toast if mutation doesn't have its own error handler
        if (!mutation.options.onError && !parsedError.isValidationError) {
          // const icon = getErrorIcon(parsedError.category);
          // toast.error(
          //   <div className="flex items-start gap-2">
          //     <span>{icon}</span>
          //     <span>{parsedError.message}</span>
          //   </div>,
          // );
        }
      },
    }),

    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        retry: (failureCount, error) => {
          const parsedError = parseApiError(error);

          // Don't retry these categories
          if (
            parsedError.isAuthError ||
            parsedError.isAuthorizationError ||
            parsedError.isValidationError ||
            parsedError.isNotFoundError ||
            parsedError.isCancelledError
          ) {
            return false;
          }

          return failureCount < 3;
        },
        retryDelay: (attemptIndex) =>
          Math.min(1000 * Math.pow(2, attemptIndex), 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
};

// const getErrorIcon = (category: ErrorCategory): string => {
//   const icons: Record<ErrorCategory, string> = {
//     [ErrorCategory.NETWORK]: "🌐",
//     [ErrorCategory.TIMEOUT]: "⏰",
//     [ErrorCategory.AUTHENTICATION]: "🔐",
//     [ErrorCategory.AUTHORIZATION]: "🚫",
//     [ErrorCategory.VALIDATION]: "⚠️",
//     [ErrorCategory.NOT_FOUND]: "🔍",
//     [ErrorCategory.RATE_LIMIT]: "⏱️",
//     [ErrorCategory.SERVER]: "🔧",
//     [ErrorCategory.CLIENT]: "❌",
//     [ErrorCategory.CANCELLED]: "✋",
//     [ErrorCategory.UNKNOWN]: "❓",
//   };
//   return icons[category] || "❌";
// };

export const QueryProvider: React.FC<QueryProviderProps> = ({
  children,
  onAuthError,
}) => {
  const [queryClient] = useState(() => createQueryClient(onAuthError));

  useEffect(() => {
    if (onAuthError) {
      globalErrorHandler.setAuthErrorCallback(onAuthError);
    }
    return () => {
      globalErrorHandler.clearAuthErrorCallback();
    };
  }, [onAuthError]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  );
};
